import { listIterator, listReverseIterator } from '@rivethealth/core/list';

/**
 * Generate self-contained HTML for an element.
 */
export function elementToStyledHtml(original: HTMLElement): string {
  let result = '';
  result += '<!DOCTYPE html>\n';
  result += '<html>\n';
  result += '<head>\n';
  result += '<meta charset="utf-8"/>\n';
  result += '<title>Rivet</title>\n';
  result += `<style>
    html, body { margin: 0; padding: 0; width: 100%; min-height: 960px; }
  </style>\n`;

  // const rootCss = getComputedStyle(element).cssText;
  const rootCss = `font-family: ${getComputedStyle(original).fontFamily}`;
  result += `<style>body > * { ${rootCss} }</style>\n`;

  result += '<style>\n';
  for (const rule of cssRules(original)) {
    result += `${rule.cssText}\n`;
  }
  result += '</style>\n';

  result += '</head>\n';
  result += '<body style="-webkit-print-color-adjust:exact;">\n';

  const element = clone(original);
  element.style.minHeight = '960px'; // assumes 0.5 inch margin
  element.style.width = '100%';
  result += element.outerHTML;

  result += '</body>\n';
  result += '</html>\n';

  return result;
}

/**
 * Clone elements and text.
 */
function clone(original: HTMLElement): HTMLElement {
  const element = <HTMLElement>original.cloneNode();

  // remove attributes
  for (const attribute of listReverseIterator(element.attributes)) {
    if (attribute.name.startsWith('ng-') && !attribute.namespaceURI) {
      element.attributes.removeNamedItem(attribute.name);
    }
  }

  // add children
  for (const child of listIterator(original.childNodes)) {
    // exclude comments
    switch (child.nodeType) {
      case Node.ELEMENT_NODE:
        element.appendChild(clone(<HTMLElement>child));
        break;
      case Node.TEXT_NODE:
        element.appendChild(child.cloneNode());
        break;
    }
  }
  return element;
}

/**
 * Find all CSS rules that match the element or descendants.
 * This may be a premature optimization.
 */
function* cssRules(element: HTMLElement): IterableIterator<CSSRule> {
  for (const stylesheet of listIterator(element.ownerDocument.styleSheets)) {
    let rules: CSSRuleList;
    try {
      rules = (<CSSStyleSheet>stylesheet).cssRules;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`Could not read stylesheet: ${e}`); // CORS
      continue;
    }
    for (const rule of listIterator(rules)) {
      switch (rule.constructor.name) {
        case 'CSSFontFaceRule':
        case 'CSSMediaRule':
        case 'CSSPageRule':
        case 'CSSLayerBlockRule':
          yield rule;
          break;
        case 'CSSStyleRule': {
          const selector = (<CSSStyleRule>rule).selectorText;
          let matches: boolean;
          try {
            matches =
              element.matches(selector) ||
              element.querySelector(selector) != null;
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn(`Invalid selector ${selector}: ${e}`); // Browser-specific selectors
            continue;
          }
          if (matches) {
            yield rule;
          }
          break;
        }
      }
    }
  }
}
